'use client';

import { FormEvent, useState } from 'react';

import { useConfig } from '@/app/contexts/ConfigContext';
import Button from '@/components/Button';
import { getWordingWithdrawal, isWithdrawalOnlyHost } from '@/helpers';

const WITHDRAWAL_EMAIL = 'withdrawal@serialscreener.com';

const WITHDRAWAL_SERVICE_CONTACT_EMAIL_HOSTS = new Set([
  'frportal.myfitgoal.net',
  'frportal.mypilatescoach.net',
  'frportal.zenzone.club',
  'frportal.astrolia.org',
  'frportal.lovara.net',
  'tomportal.myfitgoal.net',
  'tomportal.mypilatescoach.net',
  'tomportal.zenzone.club',
  'domportal.myfitgoal.net',
  'domportal.mypilatescoach.net',
  'domportal.zenzone.club',
]);

export default function Withdrawal() {
  const { config, selectedLanguage } = useConfig();
  const selectedLanguageWording = getWordingWithdrawal(selectedLanguage);
  const isWithdrawalOnlyPortal = isWithdrawalOnlyHost(
    config.SERVICE_URL_LINK,
  );
  const withdrawalEmail = WITHDRAWAL_SERVICE_CONTACT_EMAIL_HOSTS.has(
    config.SERVICE_URL_LINK,
  )
    ? config.SERVICE_CONTACT_EMAIL
    : WITHDRAWAL_EMAIL;

  // const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setInfo('');
    setError('');

    const trimmedComment = comment.trim();

    if (/*!phone ||*/ !email || !trimmedComment) {
      setError(selectedLanguageWording.errorMessage);
      return;
    }

    const subject = encodeURIComponent(
      `${config.SERVICE_URL_LINK}${
        isWithdrawalOnlyPortal ? ` / ${config.SERVICE_NAME}` : ''
      } - ${selectedLanguageWording.pageTitle}`,
    );
    const body = encodeURIComponent(
      [
        `${selectedLanguageWording.serviceLabel}: ${config.SERVICE_NAME}`,
        `${selectedLanguageWording.serviceUrlLabel}: ${config.SERVICE_URL_LINK}`,
        // `${selectedLanguageWording.phoneInputLabel}: ${phone}`,
        `${selectedLanguageWording.emailInputLabel}: ${email}`,
        '',
        `${selectedLanguageWording.commentInputLabel}:`,
        trimmedComment,
        '',
        '',
        '',
        selectedLanguageWording.autogeneratedEmailNotice,
      ].join('\n'),
    );

    try {
      window.location.href = `mailto:${withdrawalEmail}?subject=${subject}&body=${body}`;

      setInfo(selectedLanguageWording.successMessage);
      // setPhone('');
      setEmail('');
      setComment('');
    } catch {
      setError(selectedLanguageWording.errorMessage);
    }
  };

  return (
    <main className='max-w-[1920px] mx-auto overflow-hidden'>
      <div className='min-h-screen sm:p-8 px-4 py-8'>
        <div className='max-w-4xl mx-auto'>
          <div
            className='rounded-[28px] border p-6 sm:p-10 shadow-[0_20px_60px_rgba(15,23,42,0.08)]'
            style={{
              borderColor: `${config.theme.textColorDark}20`,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
            }}
          >
            <div className='max-w-2xl'>
              <p
                className='text-sm font-semibold uppercase tracking-[0.24em] mb-3'
                style={{ color: config.theme.accentColorOne }}
              >
                {selectedLanguageWording.badgeLabel}
              </p>
              <h1
                className='text-3xl sm:text-4xl font-bold mb-4'
                style={{ color: config.theme.textColorDark }}
              >
                {selectedLanguageWording.pageTitle}
              </h1>
              <p
                className='text-base sm:text-lg leading-7 mb-8'
                style={{ color: config.theme.textColorDark }}
              >
                {selectedLanguageWording.description}
              </p>
            </div>

            <form className='grid gap-5' onSubmit={handleSubmit}>
              {/*<div className='grid gap-2'>
                <label
                  htmlFor='withdrawal-phone'
                  className='text-sm font-semibold'
                  style={{ color: config.theme.textColorDark }}
                >
                  {selectedLanguageWording.phoneInputLabel}
                </label>
                <input
                  id='withdrawal-phone'
                  type='tel'
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  className='w-full rounded-2xl border px-4 py-4 outline-none transition'
                  style={{
                    borderColor: `${config.theme.textColorDark}26`,
                    color: config.theme.textColorDark,
                  }}
                  inputMode='numeric'
                  autoComplete='tel'
                  pattern='[0-9]+'
                  placeholder={selectedLanguageWording.phoneInputPlaceholder}
                  required
                />
              </div>*/}

              <div className='grid gap-2'>
                <label
                  htmlFor='withdrawal-email'
                  className='text-sm font-semibold'
                  style={{ color: config.theme.textColorDark }}
                >
                  {selectedLanguageWording.emailInputLabel}
                </label>
                <input
                  id='withdrawal-email'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className='w-full rounded-2xl border px-4 py-4 outline-none transition'
                  style={{
                    borderColor: `${config.theme.textColorDark}26`,
                    color: config.theme.textColorDark,
                  }}
                  autoComplete='email'
                  placeholder={selectedLanguageWording.emailInputPlaceholder}
                  required
                />
              </div>

              <div className='grid gap-2'>
                <label
                  htmlFor='withdrawal-comment'
                  className='text-sm font-semibold'
                  style={{ color: config.theme.textColorDark }}
                >
                  {selectedLanguageWording.commentInputLabel}
                </label>
                <textarea
                  id='withdrawal-comment'
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className='min-h-40 w-full rounded-2xl border px-4 py-4 outline-none transition resize-y'
                  style={{
                    borderColor: `${config.theme.textColorDark}26`,
                    color: config.theme.textColorDark,
                  }}
                  placeholder={selectedLanguageWording.commentInputPlaceholder}
                  required
                />
              </div>

              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2'>
                <Button itemType='submit' variant='secondary'>
                  {selectedLanguageWording.submitButtonLabel}
                </Button>
              </div>

              {info && (
                <div
                  className='text-sm font-medium mt-2'
                  style={{ color: config.theme.accentColorOne }}
                >
                  {info}
                </div>
              )}
              {error && (
                <div className='text-sm font-medium mt-2 text-red-500'>
                  {error}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
